import { formatMoney } from "@/shared/format";
import type { BonificoSelezione } from "../bonificoSelezione";
interface OrdiniListHeaderProps {
  totalVisible: number;
  totalAll: number;
  selectedCount: number;
  masterState: "unchecked" | "indeterminate" | "checked";
  onMasterChange: (checked: boolean) => void;
  /** Quanto c'è da bonificare al fornitore per gli ordini selezionati
   *  (IVA inclusa). Assente = niente selezione, niente riga. */
  bonifico?: BonificoSelezione;
}

export function OrdiniListHeader({
  totalVisible,
  totalAll,
  selectedCount,
  masterState,
  onMasterChange,
  bonifico,
}: OrdiniListHeaderProps) {
  const isChecked = masterState === "checked";
  const isIndeterminate = masterState === "indeterminate";

  function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
    onMasterChange(e.target.checked);
  }

  const masterLabel =
    selectedCount === 0
      ? "0 selezionati"
      : `${selectedCount} selezionat${selectedCount === 1 ? "o" : "i"}`;

  return (
    <div className="flex items-center justify-between mb-3 gap-[14px] max-md:flex-wrap">
      {/* Left: master checkbox + title */}
      <div className="flex items-center gap-3">
        <label
          className={[
            "inline-flex items-center gap-[10px] cursor-pointer select-none",
            isIndeterminate ? "is-indeterminate" : "",
          ].join(" ")}
          aria-label="Seleziona tutti gli ordini"
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={isChecked}
            onChange={handleCheckboxChange}
            aria-label="Seleziona tutti"
            data-testid="master-checkbox"
          />
          <span
            className={[
              "w-5 h-5 border-[1.5px] rounded-[5px] inline-grid place-items-center transition-all duration-[120ms] relative",
              isChecked || isIndeterminate ? "bg-accent border-accent" : "border-line-2 bg-card",
            ].join(" ")}
            aria-hidden="true"
          >
            {isIndeterminate ? (
              <span className="w-[10px] h-0.5 bg-card rounded-[1px] block" />
            ) : (
              <svg
                className={[
                  "w-[13px] h-[13px] fill-none stroke-white stroke-[3] [stroke-linecap:round] [stroke-linejoin:round] transition-opacity",
                  isChecked ? "opacity-100" : "opacity-0",
                ].join(" ")}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </span>
          <h2 className="font-display text-[20px] font-semibold tracking-[-0.015em]">
            Lista ordini
          </h2>
        </label>

        <span
          className="text-[11.5px] text-ink tabular-nums"
          aria-live="polite"
          aria-label={masterLabel}
        >
          {masterLabel}
        </span>

        {/* Quanto bonificare per la selezione. Compare solo con una selezione
            attiva: a zero ordini sarebbe rumore. Dichiara sempre quanti
            ordini NON sono nel totale — un importo che ingloba in silenzio
            i costi ignoti e' un importo sbagliato che sembra giusto. */}
        {bonifico && selectedCount > 0 && (
          <span
            className="flex flex-wrap items-baseline gap-1.5 border-l border-line pl-3"
            aria-live="polite"
            data-testid="bonifico-selezione"
          >
            <span className="text-[11.5px] text-ink">da bonificare</span>
            <span className="text-[14px] font-medium tabular-nums text-ink">
              {formatMoney(bonifico.totaleLordo)}
            </span>
            <span className="text-[10.5px] text-ink">IVA incl.</span>
            {bonifico.numeri.length > 0 && (
              <span className="text-[11.5px] tabular-nums text-ink" data-testid="bonifico-numeri">
                · {bonifico.numeri.join(", ")}
              </span>
            )}
            {bonifico.senzaCosto > 0 && (
              <span className="text-[11px] text-red" title="Costo fornitore non ancora noto">
                · {bonifico.senzaCosto} senza costo
              </span>
            )}
          </span>
        )}
      </div>

      {/* Right: meta (il toggle vista editoriale/compatta e' stato rimosso —
          la lista ha un'unica griglia a colonne fisse, F2 2026-07-14). */}
      <div className="text-[12px] text-ink">
        <strong className="font-medium text-ink tabular-nums">{totalVisible}</strong> visibili su{" "}
        <strong className="font-medium text-ink tabular-nums">{totalAll}</strong>
      </div>
    </div>
  );
}
